import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Delete, DragIndicator, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CustomEvalTestDefinition } from '../graphql/settings';
import { GENERATE_CUSTOM_TESTS_FROM_PROMPT_MUTATION } from '../graphql/sequences';

function newId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ensureCustomTestIds(tests: CustomEvalTestDefinition[]): CustomEvalTestDefinition[] {
  return tests.map((t) => ({
    ...t,
    id: t.id && String(t.id).length > 0 ? String(t.id) : newId(),
  }));
}

/** Map legacy stored shapes to current API; strip disallowed ``model`` on LLM tests. */
export function sanitizeCustomTestsForPersistence(
  tests: ReadonlyArray<CustomEvalTestDefinition & { type?: string; model?: string }>,
): CustomEvalTestDefinition[] {
  return tests.map((t) => {
    const type =
      (t as { type?: string }).type === 'ollama_assert' ? 'llm_assert' : (t.type as CustomEvalTestDefinition['type']);
    const base = { ...t, type } as CustomEvalTestDefinition;
    if (base.type !== 'llm_assert') {
      return base;
    }
    const copy = { ...base };
    delete (copy as { model?: string }).model;
    return copy;
  });
}

const TYPE_LABELS: Record<CustomEvalTestDefinition['type'], string> = {
  range: 'Numeric range',
  contains_text: 'Text contains',
  json_contains: 'JSON structure',
  llm_assert: 'LLM assertion',
};

function blankTest(kind: CustomEvalTestDefinition['type']): CustomEvalTestDefinition {
  const id = newId();
  switch (kind) {
    case 'range':
      return { id, type: 'range', path: 'output.score', min: 0, max: 100 };
    case 'contains_text':
      return { id, type: 'contains_text', path: 'output.analysis_text', substring: '', case_insensitive: false };
    case 'json_contains':
      return { id, type: 'json_contains', path: 'output', subset: { score: 10 } };
    case 'llm_assert':
      return {
        id,
        type: 'llm_assert',
        prompt:
          'Given the model output below, reply with JSON only: {"score": <number 0-10>}.\n\nOutput:\n{{analysis_text}}',
        expect: { type: 'json_contains', subset: { score: 10 } },
      };
    default:
      return { id, type: 'range', path: 'output.score', min: 0, max: 100 };
  }
}

interface SortableRowProps {
  test: CustomEvalTestDefinition;
  onChange: (next: CustomEvalTestDefinition) => void;
  onDelete: () => void;
}

function SortableTestRow({ test, onChange, onDelete }: SortableRowProps) {
  const sid = test.id ?? '__missing__';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sid });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
  };

  const update = (patch: Partial<CustomEvalTestDefinition>) => {
    onChange({ ...test, ...patch });
  };

  const subsetStr = (() => {
    try {
      return JSON.stringify(test.subset ?? {}, null, 2);
    } catch {
      return '{}';
    }
  })();

  const expectSubsetStr = (() => {
    try {
      return JSON.stringify(test.expect?.subset ?? {}, null, 2);
    } catch {
      return '{}';
    }
  })();

  return (
    <Paper ref={setNodeRef} style={style} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <IconButton size="small" sx={{ cursor: 'grab', mt: 0.5 }} {...attributes} {...listeners} aria-label="Drag to reorder">
          <DragIndicator fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {TYPE_LABELS[test.type]}
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Test id (optional)"
              value={test.id || ''}
              onChange={(e) => update({ id: e.target.value || undefined })}
              onBlur={() => {
                if (!test.id?.trim()) {
                  update({ id: newId() });
                }
              }}
              helperText="Stable id shown in results"
            />

            {test.type !== 'llm_assert' && (
              <TextField
                size="small"
                fullWidth
                label="JSON path"
                value={test.path || ''}
                onChange={(e) => update({ path: e.target.value })}
                helperText="e.g. output.analysis_text, output.metadata, input.messages"
                required
              />
            )}

            {test.type === 'range' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  label="Min (optional)"
                  type="number"
                  value={test.min ?? ''}
                  onChange={(e) => update({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Max (optional)"
                  type="number"
                  value={test.max ?? ''}
                  onChange={(e) => update({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
                  fullWidth
                />
              </Stack>
            )}

            {test.type === 'contains_text' && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  label="Substring"
                  value={test.substring || ''}
                  onChange={(e) => update({ substring: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!test.case_insensitive}
                      onChange={(e) => update({ case_insensitive: e.target.checked })}
                    />
                  }
                  label="Case insensitive"
                />
              </>
            )}

            {test.type === 'json_contains' && (
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={3}
                label="Expected JSON subset"
                value={subsetStr}
                onChange={(e) => {
                  const v = e.target.value;
                  try {
                    update({ subset: JSON.parse(v || '{}') });
                  } catch {
                    update({ subset: v as unknown });
                  }
                }}
                helperText="Object must appear within the value at path (partial match)."
              />
            )}

            {test.type === 'llm_assert' && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                  label="Prompt (LLM)"
                  value={test.prompt || ''}
                  onChange={(e) => update({ prompt: e.target.value })}
                  helperText="Placeholders: {{input_json}}, {{output_json}}, {{analysis_text}}"
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Assert on LLM response</InputLabel>
                  <Select
                    label="Assert on LLM response"
                    value={test.expect?.type || 'contains_text'}
                    onChange={(e) =>
                      update({
                        expect: {
                          type: e.target.value as 'contains_text' | 'json_contains',
                          substring: test.expect?.substring,
                          case_insensitive: test.expect?.case_insensitive,
                          subset: test.expect?.subset,
                        },
                      })
                    }
                  >
                    <MenuItem value="contains_text">Text contains</MenuItem>
                    <MenuItem value="json_contains">JSON contains subset</MenuItem>
                  </Select>
                </FormControl>
                {test.expect?.type === 'contains_text' && (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      label="Substring"
                      value={test.expect?.substring || ''}
                      onChange={(e) =>
                        update({
                          expect: { ...test.expect!, type: 'contains_text', substring: e.target.value },
                        })
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!test.expect?.case_insensitive}
                          onChange={(e) =>
                            update({
                              expect: {
                                ...test.expect!,
                                type: 'contains_text',
                                case_insensitive: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label="Case insensitive"
                    />
                  </>
                )}
                {test.expect?.type === 'json_contains' && (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    label="Expected JSON subset in LLM reply"
                    value={expectSubsetStr}
                    onChange={(e) => {
                      const v = e.target.value;
                      try {
                        update({
                          expect: { ...test.expect!, type: 'json_contains', subset: JSON.parse(v || '{}') },
                        });
                      } catch {
                        update({
                          expect: { ...test.expect!, type: 'json_contains', subset: v as unknown },
                        });
                      }
                    }}
                  />
                )}
              </>
            )}
          </Stack>
        </Box>
        <IconButton size="small" color="error" onClick={onDelete} aria-label="Remove test">
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

interface CustomEvaluationTestBuilderProps {
  tests: CustomEvalTestDefinition[];
  onChange: (tests: CustomEvalTestDefinition[]) => void;
  /** 'sequence' = after each sequence run; 'evaluation_type' = default tests for API POST evaluation_key */
  variant?: 'evaluation_api' | 'evaluation_type' | 'sequence';
  /** When set, show LLM prompt helper to draft tests (backend Ollama/RunPod). */
  generateFromPromptContext?: 'sequence' | 'api_evaluation';
}

function graphqlErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'graphQLErrors' in err) {
    const ge = (err as { graphQLErrors?: { message?: string }[] }).graphQLErrors;
    if (Array.isArray(ge) && ge[0]?.message) return ge[0].message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

const CustomEvaluationTestBuilder: React.FC<CustomEvaluationTestBuilderProps> = ({
  tests,
  onChange,
  variant = 'evaluation_api',
  generateFromPromptContext,
}) => {
  const [llmPrompt, setLlmPrompt] = useState('');
  const [appendGenerated, setAppendGenerated] = useState(true);
  const [genError, setGenError] = useState<string | null>(null);

  const [generateTests, { loading: generatingTests }] = useMutation(GENERATE_CUSTOM_TESTS_FROM_PROMPT_MUTATION);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = tests.map((t) => t.id ?? '__missing__');

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = ids.findIndex((x) => x === String(active.id));
    const newIndex = ids.findIndex((x) => x === String(over.id));
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onChange(arrayMove(tests, oldIndex, newIndex));
  };

  const replaceAt = (index: number, next: CustomEvalTestDefinition) => {
    const copy = [...tests];
    copy[index] = next;
    onChange(copy);
  };

  const removeAt = (index: number) => {
    onChange(tests.filter((_, i) => i !== index));
  };

  const addKind = (kind: CustomEvalTestDefinition['type']) => {
    onChange([...tests, blankTest(kind)]);
  };

  const handleGenerateFromPrompt = async () => {
    if (!generateFromPromptContext) return;
    setGenError(null);
    const prompt = llmPrompt.trim();
    if (!prompt) {
      setGenError('Describe what you want to assert first.');
      return;
    }
    try {
      const { data } = await generateTests({
        variables: {
          input: {
            prompt,
            context: generateFromPromptContext,
          },
        },
      });
      const raw = data?.generateCustomTestsFromPrompt?.tests as CustomEvalTestDefinition[] | undefined;
      const generated = Array.isArray(raw) ? raw : [];
      if (generated.length === 0) {
        setGenError('No tests were returned; try rephrasing your prompt.');
        return;
      }
      const sanitized = sanitizeCustomTestsForPersistence(generated);
      const withIds = ensureCustomTestIds(sanitized);
      onChange(appendGenerated ? [...tests, ...withIds] : withIds);
    } catch (e: unknown) {
      setGenError(graphqlErrorMessage(e));
    }
  };

  const helper =
    variant === 'sequence' ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag tests by the handle to reorder. They run after each successful sequence execution. Use JSON paths under{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          input
        </Box>{' '}
        (final variables) and{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          output
        </Box>{' '}
        — for example{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          output.analysis_text
        </Box>
        ,{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          output.steps
        </Box>
        .
      </Typography>
    ) : variant === 'evaluation_type' ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These tests run on sampled API evaluation requests when the POST body omits{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          custom_tests
        </Box>
        . Paths use{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          input
        </Box>{' '}
        and{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          output
        </Box>{' '}
        from the evaluation payload (for example{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          output.analysis_text
        </Box>
        ).
      </Typography>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag tests by the handle to reorder. Definitions are stored on the evaluation API key and run for each sampled request.
        Override per request with a{' '}
        <Box component="code" sx={{ bgcolor: '#f0f4f8', px: 0.5, borderRadius: 0.5 }}>
          custom_tests
        </Box>{' '}
        array in the POST body.
      </Typography>
    );

  return (
    <Box>
      {helper}
      {generateFromPromptContext && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderColor: 'rgba(30, 58, 95, 0.18)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            Draft tests from a prompt (LLM)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Uses your backend LLM (Ollama via RunPod). Describe checks in plain language; you can edit results below.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder={
              generateFromPromptContext === 'sequence'
                ? 'e.g. Fail if overall harm score in output is above 80, and analysis_text must mention "refund"'
                : 'e.g. Require output.analysis_text to contain "disclaimer" and input.role equals "user"'
            }
            value={llmPrompt}
            onChange={(e) => setLlmPrompt(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: genError ? 1 : 0 }}>
            <Button
              variant="contained"
              size="small"
              disabled={generatingTests}
              startIcon={generatingTests ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerateFromPrompt}
              sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}
            >
              {generatingTests ? 'Generating…' : 'Generate'}
            </Button>
            <FormControlLabel
              control={<Checkbox size="small" checked={appendGenerated} onChange={(e) => setAppendGenerated(e.target.checked)} />}
              label={<Typography variant="body2">Append to existing tests</Typography>}
            />
          </Stack>
          {genError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setGenError(null)}>
              {genError}
            </Alert>
          )}
        </Paper>
      )}
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" onClick={() => addKind('range')}>
          + Range
        </Button>
        <Button size="small" variant="outlined" onClick={() => addKind('contains_text')}>
          + Text
        </Button>
        <Button size="small" variant="outlined" onClick={() => addKind('json_contains')}>
          + JSON
        </Button>
        <Button size="small" variant="outlined" onClick={() => addKind('llm_assert')}>
          + LLM
        </Button>
      </Stack>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {tests.map((t, index) => (
            <SortableTestRow
              key={t.id}
              test={t}
              onChange={(next) => replaceAt(index, next)}
              onDelete={() => removeAt(index)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {tests.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
          <Typography color="text.secondary">No custom tests yet. Add one from the buttons above.</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CustomEvaluationTestBuilder;
